import React from 'react';
import Skeleton from './Skeleton';

/**
 * RecruitmentSkeleton
 * Shimmer placeholder for Recruitment & Onboarding:
 *   - header (title + three actions)
 *   - 5 KPI tiles
 *   - candidate list (left) + candidate detail with tabs (right)
 *
 * The layout mirrors the real page's grid so nothing jumps when the data lands.
 * Built from the base <Skeleton /> block (pure Tailwind shimmer).
 */

const CARD = 'bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-700 rounded-2xl';

const S = Skeleton;

const KpiSkeleton = () => (
  <div className={`${CARD} p-4`}>
    <div className="flex items-center justify-between">
      <S className="h-3 w-20 rounded-md" />
      <S className="h-4 w-4 rounded" />
    </div>
    <S className="h-7 w-10 rounded-lg mt-2" />
  </div>
);

const CandidateRowSkeleton = () => (
  <div className="flex items-start gap-3 p-4 border-b border-[#F1F5F9] dark:border-gray-800">
    <S className="w-9 h-9 rounded-full shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <S className="h-3.5 w-28 rounded-md" />
        <S className="h-5 w-16 rounded-full" />
      </div>
      <S className="h-3 w-20 rounded-md" />
      <div className="flex gap-3 pt-0.5">
        <S className="h-3 w-9 rounded-md" />
        <S className="h-3 w-9 rounded-md" />
        <S className="h-3 w-9 rounded-md" />
      </div>
    </div>
  </div>
);

/** The card + field grid used by most of the detail tabs. */
export const RecruitmentPanelSkeleton = ({ rows = 3 }) => (
  <div className={`${CARD} p-5`}>
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="space-y-2">
        <S className="h-4 w-48 rounded-md" />
        <S className="h-3 w-72 max-w-full rounded-md" />
      </div>
      <S className="h-6 w-20 rounded-md" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      {Array.from({ length: rows * 2 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <S className="h-2.5 w-24 rounded" />
          <S className="h-10 w-full rounded-xl" />
        </div>
      ))}
    </div>
  </div>
);

const DetailSkeleton = () => (
  <div className={`${CARD} overflow-hidden`}>
    {/* Candidate header */}
    <div className="p-5 border-b border-[#E3E9F3] dark:border-gray-700">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <S className="w-12 h-12 rounded-full shrink-0" />
          <div className="space-y-2">
            <S className="h-5 w-40 rounded-md" />
            <S className="h-3 w-56 max-w-full rounded-md" />
            <S className="h-3 w-44 max-w-full rounded-md" />
          </div>
        </div>
        <div className="flex gap-2">
          <S className="h-[34px] w-44 rounded-[10px]" />
          <S className="h-[34px] w-32 rounded-[10px]" />
          <S className="h-[34px] w-[34px] rounded-[10px]" />
        </div>
      </div>
      <S className="h-[74px] w-full rounded-xl mt-4" />
    </div>

    {/* Tabs — widths as Tailwind classes, since <Skeleton> only takes className. */}
    <div className="flex gap-6 px-5 py-3.5 border-b border-[#E3E9F3] dark:border-gray-700">
      {['w-16', 'w-14', 'w-12', 'w-20', 'w-20'].map((w, i) => (
        <S key={i} className={`h-3.5 rounded-md ${w}`} />
      ))}
    </div>

    <div className="p-5 space-y-5">
      <RecruitmentPanelSkeleton rows={2} />
      <RecruitmentPanelSkeleton rows={3} />
    </div>
  </div>
);

const RecruitmentSkeleton = () => (
  <div className="space-y-5 pb-8">
    {/* Header */}
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
      <div className="space-y-3">
        <S className="h-7 w-72 max-w-full rounded-xl" />
        <S className="h-3.5 w-[28rem] max-w-full rounded-md" />
      </div>
      <div className="flex gap-2.5 flex-wrap">
        <S className="h-10 w-36 rounded-xl" />
        <S className="h-10 w-44 rounded-xl" />
        <S className="h-10 w-36 rounded-xl" />
      </div>
    </div>

    {/* KPI tiles */}
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)}
    </div>

    {/* List + detail */}
    <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-5 items-start">
      <aside className={`${CARD} overflow-hidden`}>
        <div className="p-4 border-b border-[#E3E9F3] dark:border-gray-700 space-y-3">
          <S className="h-4 w-24 rounded-md" />
          <S className="h-3 w-56 max-w-full rounded-md" />
          <S className="h-10 w-full rounded-xl" />
          <S className="h-10 w-full rounded-xl" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => <CandidateRowSkeleton key={i} />)}
      </aside>

      <DetailSkeleton />
    </div>
  </div>
);

export default RecruitmentSkeleton;
